import React, { useRef, useState, useEffect } from 'react';
import './AudioPlayer.scss';

interface Props { src: string }

const format = (s:number) =>
  `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(Math.floor(s % 60)).padStart(2,'0')}`;

const formatSpeed = (s: number) => 
  Number.isInteger(s) ? `${s}.0` : s.toString();

const speedList = [0.75, 1.0, 1.25, 1.5, 2.0]; 

const AudioPlayer:React.FC<Props> = ({ src }) => {
  const audioRef   = useRef<HTMLAudioElement|null>(null);
  const rafRef     = useRef<number>(0);

  const [playing , setPlaying ] = useState(false);
  const [duration , setDuration] = useState(0);
  const [current  , setCurrent ] = useState(0);
  const [seeking  , setSeeking ] = useState(false);

  /* 新增：倍速 */
  const [rate      , setRate]      = useState(1);    // 默认 1.0x
  const [showPanel , setShowPanel] = useState(false);

  /* -------- rAF 刷新时间 -------- */
  useEffect(() => {
    const audio = audioRef.current!;
    const loop  = () => {
      if (!seeking) setCurrent(audio.currentTime);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [seeking]);

  /* -------- 播放 / 暂停 -------- */
  const togglePlay = () => {
    const audio = audioRef.current!;
    if (playing) audio.pause();
    else         audio.play();
    setPlaying(!playing);
  };

  /* -------- ±5 秒 -------- */
  const skip = (sec:number) => {
    const audio = audioRef.current!;
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + sec));
    setCurrent(audio.currentTime);
  };

  /* -------- 拖动进度条 -------- */
  const onSeekStart = () => setSeeking(true);
  const onSeek      = (v:number) => setCurrent(v);
  const onSeekEnd   = (v:number) => {
    const audio = audioRef.current!;
    audio.currentTime = v;
    setSeeking(false);
  };

  /* -------- 倍速变更时同步到 audio -------- */
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  /* -------- 关闭面板时，如点击 wrapper 其它区域 -------- */
  useEffect(() => {
    if (!showPanel) return;
    const handler = () => setShowPanel(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [showPanel]);

  return (
    <div className="audio-wrapper">
      {/* 隐藏音频标签 */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={() => {
          const a = audioRef.current!;
          setDuration(a.duration);
          a.playbackRate = rate;         // 保证首次即按默认速度播放
        }}
        onEnded={() => setPlaying(false)}
      />

      {/* -------- 倍速选择（右上角） -------- */}
      <div
        className="speed-switch"
        onClick={e => {
          e.stopPropagation();            // 不让事件冒到 window，避免立刻被关掉
          setShowPanel(v => !v);
        }}
      >
        {formatSpeed(rate)}x
        {showPanel && (
          <ul className="speed-panel" onClick={e => e.stopPropagation()}>
            {speedList.map(v => (
              <li
                key={v}
                className={v === rate ? 'active' : ''}
                onClick={() => {
                  setRate(v);
                  setShowPanel(false);
                }}
              >
                {formatSpeed(v)}x
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* -------- 控制区 -------- */}
      <div className="controls">
        <div className="hot-area" onClick={() => skip(-5)}>
          <button className="ctrl-btn"><div className="back"></div></button>
        </div>

        <div className="hot-area" onClick={togglePlay}>
          <button className={playing ? 'action-pause' : "action-play"}></button>
        </div>

        <div className="hot-area" onClick={() => skip(5)}>
          <button className="ctrl-btn"><div className="forward"></div></button>
        </div>
      </div>

      {/* -------- 进度条 -------- */}
      <input
        className="progress"
        type="range"
        min={0}
        max={duration}
        step={0.1}
        value={current}
        onMouseDown={onSeekStart}
        onTouchStart={onSeekStart}
        onChange={e => onSeek(Number(e.target.value))}
        onMouseUp={e => onSeekEnd(Number((e.target as HTMLInputElement).value))}
        onTouchEnd={e => onSeekEnd(Number((e.target as HTMLInputElement).value))}
      />

      {/* -------- 时间 -------- */}
      <div className="time">
        <span>{format(current)}</span>
        <span>{format(duration)}</span>
      </div>
    </div>
  );
};

export default AudioPlayer;

